import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CohortMemberProgressResponse } from '../../../api/Api';
import { Icon } from '../../../assets/icons/Icon';
import styles from './EndorsementModal.module.scss';

interface EndorsementModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: CohortMemberProgressResponse;
  mutate: (data: { endorsed_user_id: string; habit_id: string; endorsement_option: string }) => void;
}

const ENDORSEMENT_OPTIONS = [
  { id: 'progress_noticeable', label: 'The progress with this habit is noticeable' },
  { id: 'positive_impacts', label: 'I can see positive impacts from this habit' },
  { id: 'helped_teammate', label: 'I have helped my teammate with this habit' },
];

const HABIT_ICONS: Array<'OneIcon' | 'TwoIcon' | 'ThreeIcon' | 'FourIcon'> = [
  'OneIcon',
  'TwoIcon',
  'ThreeIcon',
  'FourIcon',
];

// Get a consistent icon for a habit based on its id/index
const getHabitIcon = (habitId: string, index: number) => {
  const hash = habitId ? habitId.charCodeAt(0) + habitId.charCodeAt(habitId.length - 1) : index;
  return HABIT_ICONS[hash % HABIT_ICONS.length];
};

export const EndorsementModal: React.FC<EndorsementModalProps> = ({
  isOpen,
  onClose,
  member,
  mutate,
}) => {
  const [step, setStep] = useState<'habits' | 'endorse'>('habits');
  const [selectedHabit, setSelectedHabit] = useState<any>(null);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEndorseClick = (habit: any) => {
    setSelectedHabit(habit);
    setStep('endorse');
  };

  const handleBack = () => {
    setStep('habits');
    setSelectedHabit(null);
    setSelectedOption('');
    setIsDropdownOpen(false);
  };

  const handleSave = async () => {
    if (!selectedHabit || !selectedOption) return;
    
    setIsSubmitting(true);
    try {
            mutate({ 
              endorsed_user_id: member.user.id,
        habit_id: selectedHabit.habit_id,
        endorsement_option: selectedOption,
      });
        onClose();
      // Reset state
      setStep('habits');
      setSelectedHabit(null);
      setSelectedOption('');
    } catch (error) {
      console.error('Error endorsing habit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset state
    setStep('habits');
    setSelectedHabit(null);
    setSelectedOption('');
    setIsDropdownOpen(false);
  };

  const getInitials = () => {
    const first = member.user.first_name?.[0] || '';
    const last = member.user.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Endorse your teammate</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <Icon iconName="CrossIcon" width="1.25rem" height="1.25rem" />
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* User Info */}
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              {member.user.avatar_url ? (
                <img src={member.user.avatar_url} alt={member.user.full_name} />
              ) : (
                <span>{getInitials()}</span>
              )}
            </div>
            <div className={styles.userDetails}>
              <span className={styles.userName}>
                {member.user.first_name} {member.user.last_name}
              </span>
            </div>
          </div>
          {step === 'habits' && (
            <div className={styles.userSubtitle}>Developing following habits:</div>
          )}
          {step === 'habits' ? (
            /* Habits List Step */
            <div className={styles.habitsList}>
              {member.active_habits.map((habit: any, index: number) => (
                <div key={habit.habit_id || index} className={styles.habitRow}>
                  <div className={styles.habitIcon}>
                    <Icon iconName={getHabitIcon(habit.habit_id, index)} width="2.25rem" height="2.25rem" />
                    </div>
                  <span className={styles.habitName}>{habit.habit_name}</span>
                  <button
                    className={styles.endorseBtn}
                    onClick={() => handleEndorseClick({ ...habit, iconIndex: index })}
                  >
                    Endorse
                  </button>
                </div>
              ))}
            </div>
          ) : (
            /* Endorse Step */
            <div className={styles.endorseStep}>
              <div className={styles.selectedHabit}>
                <div className={styles.habitIcon}>
                  <Icon iconName={getHabitIcon(selectedHabit?.habit_id, selectedHabit?.iconIndex || 0)} width="2.25rem" height="2.25rem" />
                </div>
                <span className={styles.habitName}>{selectedHabit?.habit_name}</span>
          </div>

              <p className={styles.question}>
                Have you seen your team members complete their habit during last week?
              </p>

              <div className={styles.dropdownWrapper}>
                <label className={styles.dropdownLabel}>Endorsement option</label>
                <div 
                  className={`${styles.dropdown} ${isDropdownOpen ? styles.dropdownOpen : ''}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span className={selectedOption ? styles.dropdownValue : styles.dropdownPlaceholder}>
                    {ENDORSEMENT_OPTIONS.find(o => o.id === selectedOption)?.label || 'Select'}
                  </span>
                  <Icon 
                    iconName={isDropdownOpen ? 'ArrowUpIcon' : 'ArrowDownIcon'} 
                    width="1rem" 
                    height="1rem" 
                  />
        </div>

                {isDropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    {ENDORSEMENT_OPTIONS.map((option) => (
                      <div
                        key={option.id}
                        className={`${styles.dropdownItem} ${selectedOption === option.id ? styles.dropdownItemSelected : ''}`}
                        onClick={() => {
                          setSelectedOption(option.id);
                          setIsDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.actions}>
          <button
                  className={styles.saveBtn}
                  onClick={handleSave}
                  disabled={!selectedOption || isSubmitting}
          >
                  {isSubmitting ? 'Saving...' : 'Save'}
          </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}; 
